/**
 * @component ry-context-menu
 * @category  overlay
 * @summary   Right-click menu anchored at the cursor position. Listens for contextmenu events on a target element and opens a menu at {clientX, clientY} via a Floating UI virtual anchor. Closes on outside-click, Escape, or any menu item click.
 *
 * Composes from `<ry-popover>` (via the same FUI positioning engine). The
 * cursor-anchored virtual-element pattern is also the canonical example of
 * `createVirtualAnchor` — same approach powers slider value bubbles,
 * autocomplete caret-following, and any other "anchor at a point" overlay.
 *
 * @attribute for         CSS selector?   default=parentElement   Target element(s) whose contextmenu opens this menu. If omitted, defaults to the parent element. Pass a CSS selector to attach to multiple targets (e.g. `for=".row"`).
 * @attribute placement   'bottom-start' | 'bottom-end' | 'top-start' | ...   default="bottom-start"   Direction the menu opens from the cursor point. Auto-flips on edge.
 *
 * @property  state       'open' | 'closed'   Mirrors `data-ry-state`.
 *
 * @method    open(x, y)  Programmatically open at the given viewport coords. Useful when the contextmenu event has been intercepted elsewhere (e.g. inside another web component that suppresses the default browser handler).
 * @method    close()     Hide the menu.
 *
 * @event ry:beforeopen   detail: { x, y }   fires at the top of open(x, y) — right-click on a target or el.open(x, y) — with the viewport coords it is about to anchor to, before any state change. Not fired when already open (re-anchor).   cancelable: preventDefault() keeps the menu closed; ry:open does not fire. The browser's native context menu stays suppressed either way.
 * @event ry:open   detail: { x, y }   fires after the menu opens at the cursor position
 * @event ry:beforeclose  detail: {}         fires at the top of close() — item click, outside click, Escape, or el.close() — before any state change   cancelable: preventDefault() keeps the menu open; ry:close does not fire
 * @event ry:close  detail: {}         fires after the menu closes

 *
 * @keyboard  Escape — closes the menu when open.
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` on host.
 *
 * @content   Children become the menu body. Wrap items with `<ry-button>` or any clickable element; clicking an item closes the menu (composing the same item-click-closes pattern as ry-dropdown).
 *
 * @dom
 *   ry-context-menu[data-ry-state="open|closed"]
 *     > div.ry-context-menu__panel  [data-ry-target="panel"]   (portals to <body> on open)
 *       > {authored items}
 *
 * @gotcha  The right-click DEFAULT (the browser's native menu) is suppressed only on elements matched by `[for]` (or the parent if omitted). Right-clicks elsewhere on the page show the browser default — by design.
 *
 * @gotcha  The menu element ITSELF must not be the target — right-clicking inside the menu doesn't re-open. Outside-click closes; right-click anywhere else dismisses then opens at the new point.
 *
 * @gotcha  `open(x, y)` accepts VIEWPORT coordinates (clientX/clientY), not page coordinates. The virtual anchor is positioned relative to the viewport.
 *
 * @example basic
 * ```html
 * <div style="padding: 2rem; border: 1px dashed gray;">
 *   Right-click here
 *   <ry-context-menu>
 *     <ry-button variant="ghost">Edit</ry-button>
 *     <ry-button variant="ghost">Duplicate</ry-button>
 *     <ry-button variant="ghost">Delete</ry-button>
 *   </ry-context-menu>
 * </div>
 * ```
 *
 * @example targeted-by-selector
 * ```html
 * <ul>
 *   <li class="row">Row 1</li>
 *   <li class="row">Row 2</li>
 *   <li class="row">Row 3</li>
 * </ul>
 * <ry-context-menu for=".row">
 *   <ry-button>Action 1</ry-button>
 *   <ry-button>Action 2</ry-button>
 * </ry-context-menu>
 * ```
 *
 * @tutorial mount-creates-panel
 * ```js
 * const root = mount(`
 *   <div>
 *     <ry-context-menu><ry-button>x</ry-button></ry-context-menu>
 *   </div>
 * `);
 * const menu = root.querySelector('ry-context-menu');
 * await expectEqual(!!menu.querySelector('[data-ry-target="panel"]'), true);
 * ```
 *
 * @tutorial right-click-opens-at-cursor
 * ```js
 * const root = mount(`
 *   <div id="target">
 *     <ry-context-menu><ry-button>x</ry-button></ry-context-menu>
 *   </div>
 * `);
 * const menu = root.querySelector('ry-context-menu');
 * const target = root.querySelector('#target');
 * const fired = [];
 * menu.addEventListener('ry:open', (e) => fired.push(e.detail));
 * target.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, clientX: 100, clientY: 200, cancelable: true }));
 * await expectEqual(menu.state, 'open');
 * await expectEqual(fired[0].x, 100);
 * await expectEqual(fired[0].y, 200);
 * ```
 *
 * @tutorial item-click-closes
 * ```js
 * const root = mount(`
 *   <div id="target">
 *     <ry-context-menu>
 *       <button id="item">x</button>
 *     </ry-context-menu>
 *   </div>
 * `);
 * const menu = root.querySelector('ry-context-menu');
 * menu.open(50, 50);
 * document.querySelector('#item').click();
 * await expectEqual(menu.state, 'closed');
 * ```
 *
 * @tutorial escape-closes
 * ```js
 * const root = mount(`
 *   <div>
 *     <ry-context-menu><ry-button>x</ry-button></ry-context-menu>
 *   </div>
 * `);
 * const menu = root.querySelector('ry-context-menu');
 * menu.open(50, 50);
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
 * await expectEqual(menu.state, 'closed');
 * ```
 *
 * @tutorial right-click-suppresses-browser-default
 * ```js
 * // Wrong assumption: the browser's native menu still shows.
 * // It does NOT — preventDefault is called on the contextmenu event for
 * // matched targets.
 * const root = mount(`
 *   <div id="target">
 *     <ry-context-menu><ry-button>x</ry-button></ry-context-menu>
 *   </div>
 * `);
 * const target = root.querySelector('#target');
 * const evt = new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: 1, clientY: 1 });
 * target.dispatchEvent(evt);
 * await expectEqual(evt.defaultPrevented, true);
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the menu closed; detail carries
 * // the coords it would have anchored to. ry:open is silent.
 * const root = mount(`
 *   <div id="target">
 *     <ry-context-menu><ry-button>x</ry-button></ry-context-menu>
 *   </div>
 * `);
 * const menu = root.querySelector('ry-context-menu');
 * const opened = [];
 * menu.addEventListener('ry:open', () => opened.push(1));
 * let seen = null;
 * menu.addEventListener('ry:beforeopen', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * root.querySelector('#target').dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: 40, clientY: 60 }));
 * await expectEqual(menu.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(seen.x, 40);
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * menu.open(40, 60);
 * await expectEqual(menu.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the menu open (Escape, outside
 * // click, item click, or el.close()); ry:close is silent.
 * const root = mount(`
 *   <div>
 *     <ry-context-menu><ry-button>x</ry-button></ry-context-menu>
 *   </div>
 * `);
 * const menu = root.querySelector('ry-context-menu');
 * menu.open(50, 50);
 * const closed = [];
 * menu.addEventListener('ry:close', () => closed.push(1));
 * menu.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
 * await expectEqual(menu.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * menu.close();
 * await expectEqual(menu.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @see ry-popover
 * @see ry-dropdown
 * @see ry-menu
 */


import { RyElement } from '../core/ry-element.js';
import { attachFloating, createVirtualAnchor, type Placement } from '../core/ry-floating.js';

export class RyContextMenu extends RyElement {
  #panel: HTMLElement | null = null;
  #virtualAnchor: ReturnType<typeof createVirtualAnchor> | null = null;
  #floatingCleanup: (() => void) | null = null;
  #portalRestore: (() => void) | null = null;

  setup(): void {
    // Wrap authored children in a panel
    const authored = Array.from(this.childNodes);
    const panel = document.createElement('div');
    panel.setAttribute('data-ry-target', 'panel');
    panel.setAttribute('role', 'menu');
    panel.className = 'ry-context-menu__panel';
    panel.style.position = 'absolute';
    panel.style.top = '0';
    panel.style.left = '0';
    for (const node of authored) panel.appendChild(node);

    this.innerHTML = '';
    this.appendChild(panel);
    this.#panel = panel;

    if (!this.hasAttribute('data-ry-state')) this.state = 'closed';
    if (!this.hasAttribute('role')) this.setAttribute('role', 'presentation');

    // Attach contextmenu listeners on targets
    const selector = this.getAttribute('for');
    const targets: Element[] = selector
      ? Array.from(document.querySelectorAll(selector))
      : (this.parentElement ? [this.parentElement] : []);

    targets.forEach(target => {
      this.on(target, 'contextmenu', (e: Event) => {
        const me = e as MouseEvent;
        me.preventDefault();
        this.open(me.clientX, me.clientY);
      });
    });

    // Outside-click closes (the menu panel portals to body; treat clicks
    // inside the panel as "inside")
    this.on(document, 'click', (e: Event) => {
      if (this.state !== 'open') return;
      const t = e.target as Node | null;
      if (!t) return;
      if (this.contains(t)) return;
      if (this.#panel && this.#panel.contains(t)) {
        // Click on a menu item — close (matches ry-dropdown behavior)
        const btn = (t as Element).closest('button, [role="menuitem"], ry-button');
        if (btn) {
          this.close();
        }
        return;
      }
      this.close();
    });

    this.onEscape(() => this.close(), { skip: () => this.state !== 'open' });
  }

  open(x: number, y: number): void {
    if (this.state === 'open') {
      // Re-anchor without firing a duplicate ry:open
      this.#virtualAnchor?.update({ x, y });
      return;
    }

    if (!this.#panel) return;
    if (!this.emit('beforeopen', { x, y })) return;

    if (!this.#virtualAnchor) {
      this.#virtualAnchor = createVirtualAnchor({ x, y });

    } else {
      this.#virtualAnchor.update({ x, y });
    }

    // Mirror state onto the panel — CSS keys on its OWN [data-ry-state] so
    // visibility/transition work after portal.
    this.#panel.setAttribute('data-ry-state', 'open');
    this.#portalRestore = this.portalToBody(this.#panel);

    const placement = (this.getAttribute('placement') ?? 'bottom-start') as Placement;
    this.#floatingCleanup = attachFloating(this.#virtualAnchor.element, this.#panel, {
      placement,
      offset: 2,
      flip: true,
      shift: true,
      reflectPlacementAttribute: true,
    });

    this.state = 'open';
    this.emit('open', { x, y });
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;
    this.state = 'closed';

    if (this.#panel) this.#panel.setAttribute('data-ry-state', 'closed');

    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
    this.#portalRestore?.();
    this.#portalRestore = null;

    this.emit('close');
  }

  teardown(): void {
    this.#floatingCleanup?.();
    this.#portalRestore?.();
  }
}

customElements.define('ry-context-menu', RyContextMenu);
